import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Badge } from "components/Badge";
import { Button } from "components/Button";
import { PageWithNav } from "components/PageWithNav";
import { Spinner } from "components/Spinner";
import { SwapList } from "components/SwapList";
import api from "utils/api";
import { useToastContext } from "context/ToastContext";

const CourseManagement = ({ courses, onChange }) => {
  const [data, setData] = useState(courses ?? []);
  const [allCourses, setAllCourses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [hasError, setHasError] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const loadDropdownData = useCallback(async () => {
    try {
      setHasError(false);
      setIsLoading(true);
      const response = await api.get("/courses");
      setAllCourses([...response.data.courses.map((course) => ({ value: course.id, label: course.name }))]);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDropdownData();
  }, [loadDropdownData]);

  useEffect(() => {
    setShowForm(!!(courses && courses.length));
  }, [courses]);

  useEffect(() => {
    onChange(data.map((d, i) => ({ id: d.value || d.id, order: i })));
  }, [data, onChange]);

  if (isLoading) {
    return (
      <div className="flex justify-center">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (hasError) {
    return <p className="text-md my-3 flex justify-center font-semibold leading-3 text-red-900">Unable to load courses, please try again. If error persists, contact the developer.</p>;
  }

  return (
    <div className="mt-4 flex flex-col rounded-md border p-4">
      {!data.length && !showForm && (
        <div className="align-center flex flex-col items-center gap-4">
          <p className="text-lg font-semibold text-neutral-500">Product is empty.</p>
          <div>
            <Button size="sm" onClick={() => setShowForm(true)}>
              Attach courses +
            </Button>
          </div>
        </div>
      )}

      {!!showForm && !!allCourses.length && <SwapList values={data} options={allCourses} selectPlaceholder="Select Courses" onChange={setData} listTitle="Selected Courses" />}
    </div>
  );
};

export const ManageProduct = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const { notify } = useToastContext();

  // state
  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [data, setData] = useState({});
  const [hasError, setHasError] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);

  const loadProductData = useCallback(async () => {
    try {
      const response = await api.get(`/product/${productId}`);
      const productData = response.data.product;
      setData(productData);
      if (productData.courses) {
        setSelectedCourses(productData.courses.map((course) => ({ id: course.id, order: course.pivot.order })));
      }
    } catch {
      setHasError(true);
    } finally {
      setLoadingData(false);
    }
  }, [productId]);

  useEffect(() => {
    loadProductData();
  }, [loadProductData]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);
      await api.put(`/product/${productId}`, {
        course_data: selectedCourses,
      });
      setIsLoading(false);
      notify({ title: "Success", description: "Product updated!", type: "success" });
      navigate("/products");
    } finally {
      setIsLoading(false);
    }
  };

  const parsePrice = () => {
    const parsedPriceInfo = JSON.parse(data.price_info);
    const unitAmount = parsedPriceInfo?.unit_amount;
    const currency = parsedPriceInfo?.currency;
    return (unitAmount / 100).toFixed(2) + " " + currency?.toUpperCase();
  };

  return (
    <PageWithNav>
      {loadingData && (
        <div className="flex justify-center">
          <Spinner size="lg" color="primary" />
        </div>
      )}
      {hasError && !isLoading && <div className="flex justify-center text-xl font-semibold leading-3 text-red-900">There was a problem, refresh the page to try again.</div>}
      {!hasError && !loadingData && (
        <>
          <div className="flex w-full flex-shrink-0 flex-col rounded-md border bg-white p-4">
            <span className="text-sm text-gray-500">
              Stripe Product Information ID: <span className="font-semibold text-gray-800">{data.product_id}</span>
            </span>
            <div className="mt-2 flex flex-col space-y-1 sm:flex-row sm:items-center sm:space-x-4 sm:space-y-0">
              <h1 className="text-2xl font-semibold text-gray-800">{data.name}</h1>
            </div>
            {data.description && <p className="mt-2 text-gray-700 lg:max-w-xl">{data.description}</p>}
            <div className="mt-2">
              <span className="text-xl font-bold leading-6 text-neutral-900">{parsePrice()}</span>
            </div>

            <div className="mt-2">
              <Badge type={data.is_active ? "success" : "danger"}>{data.is_active ? "Stripe Active" : "Stripe Archived"}</Badge>
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            <CourseManagement courses={data.courses} onChange={setSelectedCourses} />

            <div className="my-4 flex gap-2">
              <Button disabled={isLoading} onClick={() => navigate("/products")} color="secondary" size="sm">
                Cancel
              </Button>
              <Button disabled={isLoading} loading={isLoading} type="submit" color="primary" size="sm">
                Save
              </Button>
            </div>
          </form>
        </>
      )}
    </PageWithNav>
  );
};
